import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/auth';
import { UpdateUserConfigs } from '../user';
import Toast from '../../components/organisms/toast';

const MAX_PINNED_TEMPLATES = 3;

interface UsePinnedTemplatesResult {
  pinnedIds: number[];
  isPinned: (templateId: number) => boolean;
  togglePin: (templateId: number) => Promise<void>;
  isUpdating: boolean;
}

/**
 * Hook for managing pinned push message / chat templates.
 * Reads from and writes to user.configs.pinnedTemplateIds.
 * Enforces a maximum of 3 pinned templates.
 */
export const usePinnedTemplates = (): UsePinnedTemplatesResult => {
  const { user, updateUser } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const isUpdatingRef = useRef(false);

  const pinnedIds: number[] = user?.configs?.pinnedTemplateIds || [];

  const isPinned = useCallback(
    (templateId: number): boolean => {
      return pinnedIds.includes(templateId);
    },
    [pinnedIds]
  );

  const togglePin = useCallback(
    async (templateId: number): Promise<void> => {
      if (isUpdatingRef.current) return;

      const currentlyPinned = isPinned(templateId);
      let newPinnedIds: number[];

      if (currentlyPinned) {
        // Unpin: remove from array
        newPinnedIds = pinnedIds.filter((id) => id !== templateId);
      } else {
        // Pin: check limit
        if (pinnedIds.length >= MAX_PINNED_TEMPLATES) {
          Toast({
            message: `You can only pin up to ${MAX_PINNED_TEMPLATES} templates. Unpin one first.`,
            intent: 'warning'
          });
          return;
        }
        newPinnedIds = [...pinnedIds, templateId];
      }

      isUpdatingRef.current = true;
      setIsUpdating(true);
      try {
        const response = await UpdateUserConfigs({
          pinnedTemplateIds: newPinnedIds
        });
        if (response.status === 'OK' && 'user' in response) {
          updateUser(response.user);
          Toast({
            message: currentlyPinned
              ? 'Template unpinned.'
              : 'Template pinned!',
            intent: 'success'
          });
        } else {
          Toast({
            message: response.message || 'Failed to update pinned templates.',
            intent: 'danger'
          });
        }
      } catch {
        Toast({
          message: 'Failed to update pinned templates.',
          intent: 'danger'
        });
      } finally {
        isUpdatingRef.current = false;
        setIsUpdating(false);
      }
    },
    [pinnedIds, isPinned, updateUser]
  );

  return {
    pinnedIds,
    isPinned,
    togglePin,
    isUpdating
  };
};

export default usePinnedTemplates;
